import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid2,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import VisibilityIcon from '@mui/icons-material/Visibility';
import React, { useState, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ReactComponent as PlusIcon } from "Assets/Icons/Settings/PlusIcon.svg";
import { ReactComponent as EditIcon } from "Assets/Icons/Settings/EditIcon.svg";
import { ReactComponent as DeleteIcon } from "Assets/Icons/Settings/DeleteIcon.svg";
import { ReactComponent as LinkedInIcon } from "Assets/Icons/Settings/LinkedInIcon.svg";
import { ReactComponent as ResumeIcon } from "Assets/Icons/Settings/ResumeIcon.svg";
import PrimaryIcon from "Components/Common/PrimaryIcon";
import CountrySelect from "Components/Common/CountrySelect";
import AddEditExperience from "./AddEditExperience";
import ConfirmationDialog from "./ConfirmationDialog";
import DeleteExperience from "./DeleteExperience";
import AddSkill from "./AddSkill";
import { updateProfile, deleteExperience as removeExperience } from "Slices/thunks";
import { toast } from "react-toastify";
import axios from "Services/api";
import { api } from "config";
import { useFormik } from "formik";
import * as yup from "yup";


const styles = (theme) => ({
  display: "flex",
  flexDirection: "column",
  gap: "20px",
  border: `1px solid ${theme.palette.border.blue}`,
  borderRadius: "8px",
  padding: "16px",
});

const AcademicJobProfile = ({ profileInfo, setSelectedTab = () => {} }) => {
  const dispatch = useDispatch();
  const options = { year: 'numeric', month: 'short' };

  const [openExperience, setOpenExperience] = useState("");
  const [deleteExperience, setDeleteExperience] = useState(false);
  const [confirmExperience, setConfirmExperience] = useState(false);
  const [addSkill, setAddSkill] = useState(false);

  const inputRef = useRef(null);

  const [skill, setSkill] = useState(
    {
      SkillID: null,
      YearID: null,
      MonthID: null,
    }
  )

  const [experience, setExperience] = useState({
    Company: null,
    JobTitle: null,
    ManageTeam: false,
    TeamSizeID: "",
    StartYearID: null,
    StartMonthID: null,
    EndYearID: null,
    EndMonthID: null,
    StartYear: null,
    StartMonth: null,
    EndYear: null,
    EndMonth: null,
    CurrentlyWork: false,
    Salary: "",
  })

  const { Common, Translation, Experiences, Skills } = useSelector((state) => ({
    Common: state.Common.common,
    Translation: state.Translation.translation?.Settings || {},
    Experiences: state.Profile.experiences,
    Skills: state.Profile.skills,
  }));

  const Schema = useMemo(() => {
    return yup.object({
      GraduationCountryID: yup.string().required(Translation?.GraduationCountryRequired || "Graduation country is required field."),
      GraduationYearID: yup.string().required(Translation?.GraduationYearRequired || "Graduation year is required field."),
      ExperienceYearID: yup.string().required(Translation?.ExperienceYearRequired || "Experience year is required field."),
      ExperienceMonthID: yup.string().required(Translation?.ExperienceMonthRequired || "Experience month is required field."),
      College: yup.string().required(Translation?.CollegeRequired || "College is required field."),
      Degree: yup.string().required(Translation?.DegreeRequired || "Degree is required field."),
      Branch: yup.string().required(Translation?.BranchRequired || "Branch is required field."),
      Headline: yup.string(),
      NoticePeriodID: yup.string()
    });
  }, [Translation]) 

  const {
    values,
    handleBlur,
    handleChange,
    handleSubmit,
    errors,
    touched,
    submitCount,
    setFieldTouched,
    setFieldValue,
  } = useFormik({
    initialValues: {
      ...profileInfo, 
      Headline: profileInfo.Headline || "", 
      NoticePeriodID: profileInfo.NoticePeriodID || ""
    },
    enableReinitialize: true,
    onSubmit: (data) => {
      const profileObj = {
        GraduationCountryID: data.GraduationCountryID,
        GraduationYearID: data.GraduationYearID,
        ExperienceYearID: data.ExperienceYearID,
        ExperienceMonthID: data.ExperienceMonthID,
        College: data.College,
        Degree: data.Degree,
        Branch: data.Branch,
        Headline: data.Headline || null,
        NoticePeriodID: data.NoticePeriodID || null,
      }
      dispatch(updateProfile(profileObj));
      setSelectedTab("");
    },
    validationSchema: Schema,
  });

  const calculateDuration = (startDate, endDate) => {
    const inputDate = new Date(startDate);
    const currentDate = endDate ? new Date(endDate) : new Date();

    let years = currentDate.getFullYear() - inputDate.getFullYear();
    let months = currentDate.getMonth() - inputDate.getMonth();

    if (months < 0) {
      years -= 1;
      months += 12;
    }

    const yearPart = `${years} ${years > 1 ? Translation?.Years : Translation?.Year}`;
    const monthPart = months > 0 ? ` ${months} ${months > 1 ? Translation?.Months : Translation?.Month}` : '';

    return yearPart + monthPart;
  }

  const handleAddSkill = () => {
    setSkill({
      SkillID: null,
      YearID: null,
      MonthID: null,
    })
    setAddSkill(true);
  }

  const handleEditSkill = (item) => {
    setSkill(item);
    setAddSkill(true);
  }

  const handleAddExperience = () => {
    setExperience({
      Company: null,
      JobTitle: null,
      ManageTeam: false,
      TeamSizeID: "",
      StartYearID: null,
      StartMonthID: null,
      EndYearID: null,
      EndMonthID: null,
      StartYear: null,
      StartMonth: null,
      EndYear: null,
      EndMonth: null,
      CurrentlyWork: false,
      Salary: "",
    })
    setOpenExperience("add");
  }

  const handleEditExperience = (exp) => {
    const expObj = {
      ...exp,
      TeamSizeID: exp.TeamSizeID || false
    }
    setExperience(expObj);
    setOpenExperience('edit');
  }

  const handleDeleteExperience = (exp) => {
    setExperience(exp);
    setDeleteExperience(true);
  }

  const onConfirmExperience = () => {
    dispatch(removeExperience({ID: experience.ID}))
    setDeleteExperience(false);
  }

  const handleUploadResume = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileType = file.name.split('.').pop();
    
    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('fileType', fileType);
      const response = await axios.post(`${api.SOCKET_URL}/FileUpload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      })

      dispatch(updateProfile({ ResumeUrl: response?.FileUpload || "" }));
    } catch (error) {
      console.log('Error: ', error);
      toast.error(Translation?.SomethingWentWrong)
    }
    e.target.value = null; // Reset the input value to allow re-uploading the same file
  };

  return (
    <>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <Grid2 sx={styles}>
          <Typography fontWeight={500} variant="title">
            {Translation?.AcademicDetails}
          </Typography>
          <Grid2
            container
            spacing={2}
            columns={{ xl: 10, lg: 8, md: 6, sm: 4, xs: 2 }}
          >
            <Grid2 size={2}>
              {" "}
              <FormControl fullWidth>
                <FormLabel color="secondary" required label>
                  {Translation?.SettingsCountry}
                </FormLabel>
                <CountrySelect
                  value={values?.GraduationCountryID}
                  error={(touched.GraduationCountryID || submitCount > 0) && errors.GraduationCountryID}
                  onBlur={() => setFieldTouched("GraduationCountryID", true)}
                  onChange={(value) => setFieldValue("GraduationCountryID", value?.ID)}
                  type="country"
                />
              </FormControl>
            </Grid2>
            <Grid2 size={2}>
              {" "}
              <FormControl fullWidth>
                <FormLabel color="secondary" required label>
                  {Translation?.GraduationYear}
                </FormLabel>
                <Autocomplete
                  options={(Common?.GraduationYear || [])}
                  autoHighlight
                  clearIcon={false}
                  getOptionLabel={(option) => option.Value}
                  value={(Common?.GraduationYear || []).find((item) => item.ID === values.GraduationYearID)}
                  onBlur={handleBlur}
                  onChange={(value, newVal) => setFieldValue("GraduationYearID", newVal?.ID)}
                  noOptionsText={<Typography>{Translation?.NoResultsFound}</Typography>}
                  renderOption={(props, option) => {
                    const { key, ...optionProps } = props;
                    return (
                      <Typography key={key} {...optionProps}>
                        {option.Value}
                      </Typography>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      error={(touched.GraduationYearID || submitCount > 0) && errors.GraduationYearID}
                      helperText={(touched.GraduationYearID || submitCount > 0) && errors.GraduationYearID}
                      name="GraduationYearID"
                      placeholder={Translation?.EnterYourGraduationYear}
                      slotProps={{
                        htmlInput: {
                          ...params.inputProps,
                          autoComplete: "new-password",
                        },
                      }}
                    />
                  )}
                />
              </FormControl>
            </Grid2>
            {/* <Grid2 size={2}>
              {" "}
              <FormControl fullWidth>
                <FormLabel color="secondary" required label>
                  {Translation?.YearsOfExperience}
                </FormLabel>
                <Box display={"flex"} gap={"20px"}>
                  <Autocomplete
                    fullWidth
                    options={(Common?.ExperienceYear || [])}
                    autoHighlight
                    clearIcon={false}
                    getOptionLabel={(option) => option.Value}
                    onBlur={handleBlur}
                    value={(Common?.ExperienceYear || []).find((item) => item.ID === values.ExperienceYearID)}
                    onChange={(value, newVal) => setFieldValue("ExperienceYearID", newVal?.ID)}
                    noOptionsText={<Typography>{Translation?.NoResultsFound}</Typography>}
                    renderOption={(props, option) => {
                      const { key, ...optionProps } = props;
                      return (
                        <Typography key={key} {...optionProps}>
                          {option.Value}
                        </Typography>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        error={(touched.ExperienceYearID || submitCount > 0) && errors.ExperienceYearID}
                        helperText={(touched.ExperienceYearID || submitCount > 0) && errors.ExperienceYearID}
                        name="ExperienceYearID"
                        placeholder={"Year"}
                        slotProps={{
                          htmlInput: {
                            ...params.inputProps,
                            autoComplete: "new-password",
                          },
                        }}
                      />
                    )}
                  />
                  <Autocomplete
                    fullWidth
                    options={(Common.ExperienceMonth || [])}
                    autoHighlight
                    clearIcon={false}
                    getOptionLabel={(option) => option.Value}
                    onBlur={handleBlur}
                    value={(Common.ExperienceMonth || []).find((item) => item.ID === values.ExperienceMonthID)}
                    onChange={(value, newVal) => setFieldValue("ExperienceMonthID", newVal?.ID)}
                    noOptionsText={<Typography>{Translation?.NoResultsFound}</Typography>}
                    renderOption={(props, option) => {
                      const { key, ...optionProps } = props;
                      return (
                        <Typography key={key} {...optionProps}>
                          {option.Value}
                        </Typography>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        error={(touched.ExperienceMonthID || submitCount > 0) && errors.ExperienceMonthID}
                        helperText={(touched.ExperienceMonthID || submitCount > 0) && errors.ExperienceMonthID}
                        name="ExperienceMonthID"
                        placeholder={"Month"}
                        slotProps={{
                          htmlInput: {
                            ...params.inputProps,
                            autoComplete: "new-password",
                          },
                        }}
                      />
                    )}
                  />
                </Box>
              </FormControl>
            </Grid2> */}
            <Grid2 size={2}>
              <FormControl fullWidth>
                <FormLabel color="secondary" required label>
                  {Translation?.YearsOfExperience}
                </FormLabel>

                <Box display={"flex"} gap={"16px"} width={"100%"}>
                  <Autocomplete
                    sx={{ flex: 1, minWidth: 0 }}
                    options={Common?.ExperienceYear || []}
                    autoHighlight
                    clearIcon={false}
                    getOptionLabel={(option) => option.Value}
                    value={(Common?.ExperienceYear || []).find(
                      (item) => item.ID === values.ExperienceYearID
                    )}
                    onBlur={handleBlur}
                    onChange={(e, newVal) =>
                      setFieldValue("ExperienceYearID", newVal?.ID)
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        placeholder="Year"
                        error={(touched.ExperienceYearID || submitCount > 0) && !!errors.ExperienceYearID}
                        helperText={(touched.ExperienceYearID || submitCount > 0) && errors.ExperienceYearID}
                      />
                    )}
                  />

                  <Autocomplete
                    sx={{ flex: 1, minWidth: 0 }}
                    options={Common?.ExperienceMonth || []}
                    autoHighlight
                    clearIcon={false}
                    getOptionLabel={(option) => option.Value}
                    value={(Common?.ExperienceMonth || []).find(
                      (item) => item.ID === values.ExperienceMonthID
                    )}
                    onBlur={handleBlur}
                    onChange={(e, newVal) =>
                      setFieldValue("ExperienceMonthID", newVal?.ID)
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        placeholder="Month"
                        error={(touched.ExperienceMonthID || submitCount > 0) && !!errors.ExperienceMonthID}
                        helperText={(touched.ExperienceMonthID || submitCount > 0) && errors.ExperienceMonthID}
                      />
                    )}
                  />
                </Box>
              </FormControl>
            </Grid2>
            <Grid2 size={2}>
              {" "}
              {/* <FormControl fullWidth>
                <FormLabel color="secondary" label required>
                  {Translation?.CollegeUniversity}
                </FormLabel>
                <Autocomplete
                  fullWidth
                  options={collegesList}
                  autoHighlight
                  clearIcon={false}
                  getOptionLabel={(option) => option.Value}
                  value={collegesList.find((item) => item.ID === values.college)}
                  renderOption={(props, option) => {
                    const { key, ...optionProps } = props;
                    return (
                      <Typography key={key} {...optionProps}>
                        {option.Value}
                      </Typography>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      error={touched.college && errors.college}
                      helperText={
                        touched.college && errors.college && errors.college
                      }
                      name="college"
                      placeholder={Translation?.EnterYourCollegeName}
                      slotProps={{
                        htmlInput: {
                          ...params.inputProps,
                          autoComplete: "new-password",
                        },
                      }}
                    />
                  )}
                />
              </FormControl> */}
              <FormControl fullWidth>
                <FormLabel color="secondary" required label>
                  {Translation?.CollegeUniversity}
                </FormLabel>
                <TextField
                  error={touched.College && errors.College}
                  helperText={touched.College && errors.College && errors.College}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="College"
                  size="small"
                  value={values.College}
                  placeholder={Translation?.EnterYourCollegeName}
                />
              </FormControl>
            </Grid2>
            <Grid2 size={2}>
              {" "}
              {/* <FormControl fullWidth>
                <FormLabel color="secondary" label required>
                  {Translation?.Degree}
                </FormLabel>
                <Autocomplete
                  fullWidth
                  options={degreesList}
                  autoHighlight
                  clearIcon={false}
                  getOptionLabel={(option) => option.Value}
                  value={degreesList.find((item) => item.ID === values.degree)}
                  renderOption={(props, option) => {
                    const { key, ...optionProps } = props;
                    return (
                      <Typography key={key} {...optionProps}>
                        {option.Value}
                      </Typography>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      error={touched.degree && errors.degree}
                      helperText={
                        touched.degree && errors.degree && errors.degree
                      }
                      name="degree"
                      placeholder={Translation?.EnterYourDegree}
                      slotProps={{
                        htmlInput: {
                          ...params.inputProps,
                          autoComplete: "new-password",
                        },
                      }}
                    />
                  )}
                />
              </FormControl> */}
              <FormControl fullWidth>
                <FormLabel color="secondary" required label>
                  {Translation?.Degree}
                </FormLabel>
                <TextField
                  error={touched.Degree && errors.Degree}
                  helperText={touched.Degree && errors.Degree && errors.Degree}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="Degree"
                  size="small"
                  value={values.Degree}
                  placeholder={Translation?.EnterYourDegree}
                />
              </FormControl>
            </Grid2>
            <Grid2 size={2}>
              {" "}
              {/* <FormControl fullWidth>
                <FormLabel color="secondary" label required>
                  {Translation?.Branch}
                </FormLabel>
                <Autocomplete
                  fullWidth
                  options={branchesList}
                  autoHighlight
                  clearIcon={false}
                  getOptionLabel={(option) => option.Value}
                  value={branchesList.find((item) => item.ID === values.branch)}
                  renderOption={(props, option) => {
                    const { key, ...optionProps } = props;
                    return (
                      <Typography key={key} {...optionProps}>
                        {option.Value}
                      </Typography>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      error={touched.branch && errors.branch}
                      helperText={
                        touched.branch && errors.branch && errors.branch
                      }
                      name="branch"
                      placeholder={Translation?.EnterYourBranch}
                      slotProps={{
                        htmlInput: {
                          ...params.inputProps,
                          autoComplete: "new-password",
                        },
                      }}
                    />
                  )}
                />
              </FormControl> */}
              <FormControl fullWidth>
                <FormLabel color="secondary" required label>
                  {Translation?.Branch}
                </FormLabel>
                <TextField
                  error={touched.Branch && errors.Branch}
                  helperText={touched.Branch && errors.Branch && errors.Branch}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  // onChange={(e) =>
                  //   setJobDetails((pre) => ({
                  //     ...pre,
                  //     Branch: e.target.value,
                  //   }))
                  // }
                  name="Branch"
                  size="small"
                  value={values.Branch}
                  placeholder={Translation?.EnterYourBranch}
                />
              </FormControl>
            </Grid2>
          </Grid2>
          <Typography fontWeight={500} variant="title">
            {Translation?.JobDetails}
          </Typography>
          <Grid2
            container
            direction={"column"}
            spacing={2}
            columns={{ xl: 10, lg: 8, md: 6, sm: 6, xs: 6 }}
          >
            <Grid2 size={6}>
              <FormControl fullWidth>
                <FormLabel color="secondary" label>
                  {Translation?.ProfileHeadline}
                </FormLabel>
                <TextField
                  size="small"
                  name="Headline"
                  error={touched.Headline && errors.Headline}
                  helperText={touched.Headline && errors.Headline && errors.Headline}
                  value={values?.Headline}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder={Translation?.ShortIntroduction}
                  inputProps={{ maxLength: 300 }}
                  multiline
                  rows={3}
                />
              </FormControl>
            </Grid2>
            <Grid2
              size={6}
              display={"flex"}
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <Box>
                <Typography fontWeight={500} variant="subtitle">
                  {Translation?.Experiences}
                </Typography>
                <Typography color="secondary.light">
                  {Translation?.ExperienceDescription}
                </Typography>
              </Box>
              <Button
                variant="outlined"
                onClick={handleAddExperience}
                startIcon={
                  <PrimaryIcon
                    icon={PlusIcon}
                    color="primary"
                    height="12px"
                    width="12px"
                  />
                }
              >
                {Translation?.AddExperiences}
              </Button>
            </Grid2>
            <Grid2
              display={"flex"}
              flexDirection={"column"}
              gap={"10px"}
              size={6}
            >
              {Array.isArray(Experiences) && Experiences?.length === 0 ? (
                <Box
                  sx={(theme) => ({
                    padding: "15px 8px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  })}
                >
                  <Typography fontWeight={500}>{Translation?.NoExperienceAdded}</Typography>
                  <Typography variant="body2" color="secondary.light">
                    {Translation?.PleaseAddExperience}
                  </Typography>
                </Box>
              ) : (
                Array.isArray(Experiences) && Experiences?.map((item, i) => {
                  return (
                    <Box
                      key={i}
                      sx={(theme) => ({
                        background: theme.palette.background.default,
                        borderRadius: "8px",
                        padding: "8px",
                      })}
                      display={"flex"}
                      flexDirection={"column"}
                      gap={"5px"}
                    >
                      <Box
                        display={"flex"}
                        alignItems={"center"}
                        justifyContent={"space-between"}
                      >
                        <Typography
                          display={"flex"}
                          gap="10px"
                          variant="subtitle"
                          fontWeight={500}
                        >
                          {/* Designer{" "} */}
                          {item.Company}
                          <Typography color="secondary.light">{calculateDuration(item?.StartDate, item?.EndDate)}</Typography>
                        </Typography>
                        <Box display={"flex"} gap="5px">
                          <IconButton
                            onClick={() => handleEditExperience(item)}
                            size="small"
                          >
                            {" "}
                            <PrimaryIcon
                              icon={EditIcon}
                              height="16px"
                              width="16px"
                            />{" "}
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteExperience(item)}
                          >
                            {" "}
                            <PrimaryIcon
                              icon={DeleteIcon}
                              height="16px"
                              width="16px"
                            />{" "}
                          </IconButton>
                        </Box>
                      </Box>
                      <Typography
                        variant="subtitle"
                        color="secondary.light"
                        fontWeight={500}
                      >
                        {/* UI UX Designer */}
                        {item.JobTitle}
                      </Typography>
                      <Typography color="secondary.light">
                        {`${new Date(item.StartDate).toLocaleString('en-US', options)} - ${item.CurrentlyWork ? Translation?.Present : new Date(item.EndDate).toLocaleString('en-US', options)}`}
                        {/* Jan 2018 - Present */}
                      </Typography>
                    </Box>
                  );
                })
              )}
            </Grid2>
            <Grid2
              size={6}
              display={"flex"}
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <Typography fontWeight={500} variant="subtitle">
                {Translation?.NoticePeriod}
              </Typography>
              <Select
                value={values?.NoticePeriodID}
                onChange={(e) => setFieldValue("NoticePeriodID", e.target.value) }
                size="small"
                sx={{ minWidth: "200px" }}
              >
                {(Common?.NoticePeriod || [])?.map((item, i) => {
                  return (
                    <MenuItem key={i} value={item?.ID}>
                      {item?.Value}
                    </MenuItem>
                  );
                })}
              </Select>
            </Grid2>
            <Grid2
              size={6}
              display={"flex"}
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <Box>
                <Typography fontWeight={500} variant="subtitle">
                  {Translation?.Skills}
                </Typography>
                <Typography color="secondary.light">
                  {Translation?.SkillDescription}
                </Typography>
              </Box>
              <Button
                variant="outlined"
                // onClick={() => setAddSkill(true)}
                onClick={handleAddSkill}
                startIcon={
                  <PrimaryIcon
                    icon={PlusIcon}
                    color="primary"
                    height="12px"
                    width="12px"
                  />
                }
              >
                {Translation?.AddSkills}
              </Button>
            </Grid2>
            <Grid2
              display={"flex"}
              flexDirection={"column"}
              gap={"10px"}
              size={6}
            >
              {Array.isArray(Skills) && Skills?.length === 0 ? (
                <Box
                  sx={(theme) => ({
                    padding: "15px 8px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  })}
                >
                  <Typography fontWeight={500}>{Translation?.NoSkillsAdded}</Typography>
                  <Typography variant="body2" color="secondary.light">
                    {Translation.PleaseAddSkills}
                  </Typography>
                </Box>
              ) : (
                Array.isArray(Skills) && Skills?.map((item, i) => {
                  return (
                    <Box
                      key={i}
                      sx={(theme) => ({
                        background: theme.palette.background.default,
                        borderRadius: "8px",
                        padding: "8px",
                      })}
                      display={"flex"}
                      flexDirection={"column"}
                      gap={"5px"}
                    >
                      <Box
                        display={"flex"}
                        alignItems={"center"}
                        justifyContent={"space-between"}
                      >
                        <Typography fontWeight={500}>{item.Skill}</Typography>
                        <Typography color="secondary.light">
                          {/* 1 year 1 month */}
                          {`${item.Year} ${item.Month}`}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })
              )}
            </Grid2>
            <Grid2
              size={6}
              display={"flex"}
              flexDirection={"column"}
              gap={"10px"}
            >
              <FormLabel
                sx={{ fontWeight: 500, fontSize: "16px" }}
                label
                required
              >
                {" "}
                {Translation?.UploadResume}
              </FormLabel>
              <Box display={"flex"} gap="15px" alignItems={"center"}>
                {/* <Button
                  startIcon={
                    <PrimaryIcon
                      width="14px"
                      height="14px"
                      icon={LinkedInIcon}
                      color="white"
                    />
                  }
                  sx={{ background: "#0076B2" }}
                  variant="contained"
                >
                  {Translation?.Connected}
                </Button>
                <Typography>{Translation?.OR}</Typography> */}
                <input type="file" ref={inputRef} onChange={handleUploadResume} accept=".pdf, .doc, .docx " style={{ display: "none" }} />
                { profileInfo?.ResumeUrl ? 
                  <Button
                    // startIcon={<VisibilityIcon />}
                    endIcon={<VisibilityIcon onClick={() =>  window.open(profileInfo?.ResumeUrl, "_blank")} />}
                    sx={(theme) => ({
                      background: theme.palette.background.default,
                      color: theme.palette.secondary.main,
                    })}
                    variant="contained"
                    // onClick={() =>  window.open(profileInfo?.ResumeUrl, "_blank")}
                    onClick={() =>  inputRef.current?.click()}
                  >
                    {/* {Translation?.View} */}
                    {Translation?.UploadAgain}
                  </Button> : 
                  <Button
                    startIcon={
                      <PrimaryIcon width="18px" height="12px" icon={ResumeIcon} />
                    }
                    sx={(theme) => ({
                      background: "#F5F5F5",
                      // background: theme.palette.background.default,
                      color: theme.palette.secondary.main,
                    })}
                    variant="contained"
                    onClick={() =>  inputRef.current?.click()}
                  >
                    {Translation?.UploadResume}
                  </Button>
                }
                {/* <Button
                  startIcon={
                    <PrimaryIcon width="18px" height="12px" icon={ResumeIcon} />
                  }
                  sx={(theme) => ({
                    background: "#F5F5F5",
                    color: theme.palette.secondary.main,
                  })}
                  variant="contained"
                  onClick={() =>  inputRef.current?.click()}
                >
                  {Translation?.UploadResume}
                </Button> */}
              </Box>
              <Typography display={"flex"}>
                <Typography fontWeight={500}> {Translation?.Note}: &nbsp;</Typography> 
                {Translation?.ResumeDescription}
              </Typography>
            </Grid2>
          </Grid2>
          <Button type="submit" variant="contained">
            {Translation?.UpdateDetails}
          </Button>
        </Grid2>
      </form>
      <AddEditExperience open={openExperience} setOpen={setOpenExperience} experience={experience} setExperience={setExperience} />
      <ConfirmationDialog
        open={confirmExperience}
        setOpen={setConfirmExperience}
      />
      <DeleteExperience open={deleteExperience} setOpen={setDeleteExperience} onConfirm={onConfirmExperience} />
      <AddSkill open={addSkill} setOpen={setAddSkill} skill={skill} setSkill={setSkill} />
    </>
  );
};

export default AcademicJobProfile;
